import { useState, useEffect, useMemo } from 'react';
import { Button, Form, Modal, Select, Space, Tag, message, Tabs, Card, Input } from 'antd';
import { resetActivityGameMatch, updateActivityGameMatch } from '../../apiBridge';
import { useOutletContext } from 'react-router-dom';
import { ActivityGameMatchInfo, ActivityOutletContext } from '../../interface';
import StateTag from './_stateTag';
import {
    MATCH_WALKOVER_REASON,
    MATCH_WIN_REASONS,
    isMatchWinner,
    matchParticipantDisplayName,
    relatedMatches,
    shouldClearMatchData,
} from './activityMatch';

interface EditMatchModalProps {
    visible: boolean;
    onCancel: () => void;
    onOk: () => void;
    currentMatch: ActivityGameMatchInfo | null;
    onMatchReset?: () => void;
    matchData: ActivityGameMatchInfo[];
}

const EditMatchModal: React.FC<EditMatchModalProps> = ({
    visible,
    onCancel,
    onOk,
    currentMatch,
    onMatchReset,
    matchData
}) => {
    const [messageApi, contextHolder] = message.useMessage();
    const [form] = Form.useForm();
    const [localParticipants, setLocalParticipants] = useState<ActivityGameMatchInfo['participants']>([]);
    const [showReason, setShowReason] = useState(false);
    const [savingState, setSavingState] = useState(false);

    const [activeTab, setActiveTab] = useState('1');
    const data = useOutletContext() as ActivityOutletContext;

    const state = Form.useWatch('state', form);
    const { nextMatch, nextLoserMatch, parentMatches } = useMemo(
        () => relatedMatches(currentMatch, matchData),
        [currentMatch, matchData],
    );

    // 获取round title的函数
    const getRoundTitle = (match: ActivityGameMatchInfo) => {
        if (!match.category_id || match.round === undefined) return null;
        
        const category = data.categories?.find(cat => String(cat.id) === String(match.category_id));
        if (!category) return `Round ${match.round + 1}`;
        
        // 否则正常显示
        return category.round_names[match.round] || `Round ${match.round + 1}`;
    };

    // 获取category name的函数
    const getCategoryName = (match: ActivityGameMatchInfo) => {
        if (!match.category_id) return null;
        return data.categories?.find(cat => String(cat.id) === String(match.category_id))?.name || 'Unknown Category';
    };

    useEffect(() => {
        setLocalParticipants(currentMatch?.participants || []);
    }, [currentMatch]);

    const handleStateChange = (value: string) => {
        // If the current match is already finished, block any state change
        if (currentMatch?.state === 'finish' && value !== 'finish') {
            Modal.warn({
                title: 'Warning',
                content: (
                    <div style={{ whiteSpace: 'pre-line' }}>
                        {"This match is finished. Use Reset Match to change it back to a playable state. Resetting permanently clears all scores, round results, match options, and timing data."}
                    </div>
                ),
                okText: 'OK',
                onOk: () => {
                    form.setFieldsValue({ state: currentMatch?.state });
                },
            });
            return;
        }

        if (value === 'finish' && currentMatch?.state !== 'finish') {
            Modal.warn({
                title: 'Warning',
                content: (
                    <div style={{ whiteSpace: 'pre-line' }}>
                        {"If no match occurred and you want to set a winner manually, please use Walkover rather than Finish."}
                    </div>
                ),
                okText: 'OK',
                onOk: () => {
                    form.setFieldsValue({ state: currentMatch?.state });
                },
            });
            return;
        }

        if (currentMatch?.state === 'bye' && value !== 'bye') {
            messageApi.warning('Cannot change state of a bye match');

            form.setFieldsValue({ state: 'bye' });
            return;
        }

        if (shouldClearMatchData(currentMatch?.state, value)) {
            Modal.confirm({
                title: 'Clear Match Data?',
                content: (
                    <div style={{ whiteSpace: 'pre-line' }}>
                        {`Changing this match from "${currentMatch?.state}" to "${value}" permanently clears all scores, round results, match options, and timing data.\n\nThis action cannot be undone. Are you sure you want to continue?`}
                    </div>
                ),
                okText: 'Clear and Change State',
                okType: 'danger',
                cancelText: 'Cancel',
                onOk: () => {
                    form.setFieldsValue({ winner_id: undefined, reason: undefined });
                    setShowReason(false);
                },
                onCancel: () => {
                    form.setFieldsValue({ state: currentMatch?.state });
                }
            });
            return;
        }

        if (currentMatch?.state === 'in_game' && value !== 'in_game') {
            Modal.confirm({
                title: 'Warning',
                content: (
                    <div style={{ whiteSpace: 'pre-line' }}>
                        {"You are attempting to modify a match with the status \"IN GAME\". \nPlease confirm that the match is not currently active to avoid data conflicts or errors. \nAre you sure you want to proceed?"}
                    </div>
                ),
                okText: 'Confirm',
                cancelText: 'Cancel',
                onOk: () => {
                    // 当切换到 walkover 时，默认填充理由为 "(WDR) Win by Withdrawal"
                    if (value === 'walkover') {
                        form.setFieldsValue({ winner_id: undefined, reason: MATCH_WALKOVER_REASON });
                        setShowReason(true);
                    } else {
                        form.setFieldsValue({ winner_id: undefined, reason: undefined });
                        setShowReason(false);
                    }
                },
                onCancel: () => {
                    form.setFieldsValue({ state: 'in_game' });
                }
            });
        } else {
            if (value === 'walkover') {
                form.setFieldsValue({ winner_id: undefined, reason: MATCH_WALKOVER_REASON });
                setShowReason(true);
            } else {
                form.setFieldsValue({ winner_id: undefined, reason: undefined });
                setShowReason(false);
            }
        }
    };

    const handleWinnerChange = (value: string) => {
        setShowReason(!!value);
        if (!value) {
            form.setFieldsValue({ reason: undefined });
        }
    };

    const handleSwapPlayers = () => {
        if (!currentMatch || !localParticipants || localParticipants.length < 2) {
            messageApi.warning('Cannot swap player positions');
            return;
        }
        
        const newParticipants = [...localParticipants];
        [newParticipants[0], newParticipants[1]] = [newParticipants[1], newParticipants[0]];
        
        setLocalParticipants(newParticipants);
        currentMatch.participants = newParticipants;
        
        const currentWrap = form.getFieldValue('wrap');
        form.setFieldsValue({ wrap: !currentWrap });
    };

    const handleEditSubmit = async () => {
        try {
            const values = await form.validateFields();
            if (!currentMatch) return;

            setSavingState(true);

            const updateData = {
                match_id: currentMatch.id,
                court: values.court,
                state: values.state,
                winner_id: values.winner_id,
                wrap: values.wrap,
                reason: values.reason,
                clear_data: shouldClearMatchData(currentMatch.state, values.state)
            };

            const res = await updateActivityGameMatch(data.activity.id, updateData);
            if (res && res.status === 200) {
                messageApi.success('Update successful');
                onMatchReset?.();
                onOk();
            } else {
                const errorData = await res.json();
                messageApi.error(errorData.error || 'Update failed');
            }

            setSavingState(false);
        } catch (error) {
            if (error instanceof Response) {
                const errorData = await error.json();
                messageApi.error(errorData.error || 'Update failed');
            } else {
                messageApi.error('Update failed');
            }
        }
    };

    const handleResetMatch = async () => {
        if (!currentMatch) return;
        
        Modal.confirm({
            title: 'Confirm Reset Match',
            content: 'Are you sure you want to reset this match? This will clear all match results.',
            okText: 'Confirm',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    const res = await resetActivityGameMatch(data.activity.id, currentMatch.id, false);
                    if (res) {
                        const responseData = await res.json();
                        if (responseData.error) {
                            messageApi.error(responseData.error);
                        } else {
                            messageApi.success('reset successful');
                            onMatchReset?.();
                            onOk();
                        }
                    }
                } catch (error) {
                    messageApi.error('Reset failed');
                }
            }
        });
    };

    useEffect(() => {
        if (visible && currentMatch) {
            form.setFieldsValue({
                court: currentMatch.court,
                state: currentMatch.state,
                winner_id: currentMatch.winner_id,
                reason: currentMatch.reason
            });
            setShowReason(false); // 初始时不显示输入框
        }
    }, [visible, currentMatch, form]);

    const formatTime = (timeStr: string | null | undefined) => {
        if (!timeStr) return '-';
        
        const utcTime = new Date(timeStr);
        const timezoneOffset = data.activity.match_tz;
        const targetTime = new Date(utcTime.getTime() + timezoneOffset * 60 * 1000);
        
        const formattedTime = targetTime.toISOString().split('T')[1].split('.')[0];
        
        return `${formattedTime} `;
    };

    const relatedPlayerStyle = (
        relatedMatch: ActivityGameMatchInfo,
        participant: ActivityGameMatchInfo['participants'][0] | undefined,
        highlight: boolean,
    ) => ({
        color: isMatchWinner(relatedMatch, participant?.id) ? '#2e7d32' : 'inherit',
        fontWeight: isMatchWinner(relatedMatch, participant?.id) ? 'bold' as const : 'normal' as const,
        backgroundColor: highlight ? 'rgba(24, 144, 255, 0.1)' : 'inherit',
        padding: '4px 8px',
        borderRadius: '4px',
    });

    return (
        <>
            {contextHolder}
            <Modal
                title="Edit Match"
                open={visible}
                onOk={handleEditSubmit}
                onCancel={onCancel}
                width={600}
                footer={null}
            >
                <Tabs
                    defaultActiveKey="1"
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={[
                        {
                            key: '1',
                            label: 'Match Info',
                            children: (
                                <Form
                                    form={form}
                                    layout="vertical"
                                    initialValues={{ wrap: false }}
                                >
                                    <Form.Item name="wrap" hidden>
                                        <input type="hidden" />
                                    </Form.Item>
                                    <Form.Item label="ID">
                                        <Space>
                                            {currentMatch?.bout_number ? <Tag>BOUT {currentMatch.bout_number}</Tag> : <></>}
                                            {currentMatch?.id}
                                        </Space>
                                        {currentMatch?.category_id && (
                                            <div style={{ marginTop: 8 }}>
                                                <Space>
                                                    <>
                                                        <Tag color="blue">{getCategoryName(currentMatch)}</Tag>
                                                        {currentMatch?.round !== undefined && (
                                                            <Tag color="purple">{getRoundTitle(currentMatch)}</Tag>
                                                        )}
                                                    </>
                                                </Space>
                                            </div>
                                        )}
                                    </Form.Item>


                                    <Form.Item label="Schedule">
                                        <Space>
                                            <Tag color="blue">Date: {currentMatch?.plan_date || '-'}</Tag>
                                            <Tag color="blue">Time: {formatTime(currentMatch?.plan_start_at)}</Tag>
                                        </Space>
                                    </Form.Item>

                                    <Form.Item
                                        name="state"
                                        label="State"
                                        rules={[{ required: true, message: 'Please select state' }]}
                                    >
                                        <Select onChange={handleStateChange} disabled={currentMatch?.state === 'bye'}>
                                            <Select.Option value="wait">Wait</Select.Option>
                                            <Select.Option value="in_game">In Game</Select.Option>
                                            <Select.Option value="finish">Finish</Select.Option>
                                            <Select.Option value="locked">Locked</Select.Option>
                                            <Select.Option value="walkover">Walkover</Select.Option>
                                            {
                                                currentMatch?.state === 'bye' && <Select.Option value="bye">Bye</Select.Option>
                                            }
                                        </Select>
                                    </Form.Item>

                                    <Form.Item>
                                        <Card size="small" bodyStyle={{ padding: '12px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                <StateTag state="locked" currentState={state} showDescription />
                                                <StateTag state="wait" currentState={state} showDescription />
                                                <StateTag state="in_game" currentState={state} showDescription />
                                                <StateTag state="finish" currentState={state} showDescription />
                                                <StateTag state="walkover" currentState={state} showDescription />
                                                {
                                                    state === 'bye' ? <StateTag state="bye" currentState={state} showDescription /> : <></>
                                                }
                                            </div>
                                        </Card>
                                    </Form.Item>

                                    {(state === 'finish' || state === 'walkover') && (
                                        <>
                                            <Form.Item
                                                name="winner_id"
                                                label="Winner"
                                                rules={[
                                                    ({ getFieldValue }) => ({
                                                        validator(_, value) {
                                                            const state = getFieldValue('state');
                                                            if ((state === 'finish' || state === 'walkover') && !value) {
                                                                return Promise.reject(new Error('Please select winner'));
                                                            }
                                                            return Promise.resolve();
                                                        },
                                                    }),
                                                ]}
                                            >
                                                <Select allowClear onChange={handleWinnerChange}>
                                                    {currentMatch?.participants.map((participant: ActivityGameMatchInfo['participants'][0]) => (
                                                        <Select.Option key={participant.id} value={participant.id}>
                                                            {participant.display_name}
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                            {!showReason && currentMatch?.reason && (
                                                <div style={{ marginBottom: 16 }}>
                                                    <div style={{ fontWeight: 'bold', marginBottom: 8 }}>Previous Reason</div>
                                                    <div style={{ 
                                                        padding: '8px 12px', 
                                                        backgroundColor: '#f5f5f5', 
                                                        borderRadius: 4,
                                                        color: '#666',
                                                        whiteSpace: 'pre-wrap'
                                                    }}>
                                                        {currentMatch.reason}
                                                    </div>
                                                </div>
                                            )}
                                            {showReason && (
                                                <>

                                                <Form.Item label="Reason">
                                                    <Select
                                                        placeholder="Quick select a reason"
                                                        onChange={(value) => {
                                                            form.setFieldsValue({ reason: value });
                                                        }}
                                                    >
                                                        {MATCH_WIN_REASONS.map((reason, index) => (
                                                            <Select.Option key={index} value={reason}>
                                                                {reason}
                                                            </Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>

                                                
                                                <Form.Item
                                                    name="reason"
                                                    rules={[{ required: true, message: 'Please input the reason for winner change' }]}
                                                >
                                                    <Input.TextArea rows={4} placeholder="Please input the reason for winner change" />
                                                </Form.Item>

                                               </>
                                            )}
                                        </>
                                    )}
                                    <div style={{ marginBottom: 24 }}>
                                        <div style={{ marginBottom: 8 }}>Player Position</div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 'bold' }}>Player 1 (Blue)</div>
                                                <div  title={localParticipants[0]?.id} style={{ color: currentMatch && isMatchWinner(currentMatch, localParticipants[0]?.id) ? '#2e7d32' : 'inherit', fontWeight: currentMatch && isMatchWinner(currentMatch, localParticipants[0]?.id) ? 'bold' : 'normal' }}>
                                                    {matchParticipantDisplayName(localParticipants[0], currentMatch?.state)}
                                                </div>
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontWeight: 'bold' }}>Player 2 (Red)</div>
                                                <div title={localParticipants[1]?.id} style={{ color: currentMatch && isMatchWinner(currentMatch, localParticipants[1]?.id) ? '#2e7d32' : 'inherit', fontWeight: currentMatch && isMatchWinner(currentMatch, localParticipants[1]?.id) ? 'bold' : 'normal' }}>
                                                    {matchParticipantDisplayName(localParticipants[1], currentMatch?.state)}
                                                </div>
                                            </div>
                                            <Button onClick={handleSwapPlayers}>Swap Position</Button>
                                        </div>
                                    </div>
                                </Form>
                            ),
                        },
                        ...(nextMatch || nextLoserMatch || parentMatches.length > 0 ? [{
                            key: '2',
                            label: 'Related Matches',
                            children: (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                    {parentMatches.length > 0 && (
                                        <Card title="Parent Matches" size="small">
                                            {parentMatches.map((parentMatch, index) => (
                                                <div key={parentMatch.id} style={{ marginBottom: index < parentMatches.length - 1 ? 16 : 0 }}>
                                                    <div style={{ marginBottom: 16 }}>
                                                        <Space>
                                                            {parentMatch.bout_number ? <Tag>BOUT {parentMatch.bout_number}</Tag> : null}
                                                            {parentMatch.court ? <Tag>Court {parentMatch.court}</Tag> : null}
                                                            <Tag color="blue">Date: {parentMatch.plan_date || '-'}</Tag>
                                                            <Tag color="blue">Time: {formatTime(parentMatch.plan_start_at)}</Tag>
                                                        </Space>
                                                        <div style={{ marginTop: 8 }}>
                                                            <Space>
                                                                {parentMatch.round !== undefined && (
                                                                    <Tag color="purple">{getRoundTitle(parentMatch)}</Tag>
                                                                )}
                                                            </Space>
                                                        </div>
                                                        <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                                                            ID: {parentMatch.id}
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 'bold' }}>Player 1</div>
                                                            <div style={relatedPlayerStyle(
                                                                parentMatch,
                                                                parentMatch.participants[0],
                                                                Boolean(
                                                                    isMatchWinner(parentMatch, parentMatch.participants[0]?.id) &&
                                                                    currentMatch?.participants.some(p => p?.id === parentMatch.participants[0]?.id)
                                                                ),
                                                            )}>
                                                                {matchParticipantDisplayName(parentMatch.participants[0], parentMatch.state)}
                                                            </div>
                                                        </div>
                                                        <div style={{ flex: 1 }}>
                                                            <div style={{ fontWeight: 'bold' }}>Player 2</div>
                                                            <div style={relatedPlayerStyle(
                                                                parentMatch,
                                                                parentMatch.participants[1],
                                                                Boolean(
                                                                    isMatchWinner(parentMatch, parentMatch.participants[1]?.id) &&
                                                                    currentMatch?.participants.some(p => p?.id === parentMatch.participants[1]?.id)
                                                                ),
                                                            )}>
                                                                {matchParticipantDisplayName(parentMatch.participants[1], parentMatch.state)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </Card>
                                    )}

                                    {nextMatch && (
                                        <Card title="Next Match (Winner)" size="small">
                                            <div style={{ marginBottom: 16 }}>
                                                <Space>
                                                    {nextMatch.bout_number ? <Tag>BOUT {nextMatch.bout_number}</Tag> : null}
                                                    {nextMatch.court ? <Tag>Court {nextMatch.court}</Tag> : null}
                                                    <Tag color="blue">Date: {nextMatch.plan_date || '-'}</Tag>
                                                    <Tag color="blue">Time: {formatTime(nextMatch.plan_start_at)}</Tag>
                                                </Space>
                                                <div style={{ marginTop: 8 }}>
                                                    <Space>
                                                        {nextMatch.round !== undefined && (
                                                            <Tag color="purple">{getRoundTitle(nextMatch)}</Tag>
                                                        )}
                                                    </Space>
                                                </div>
                                                <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                                                    ID: {nextMatch.id}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 'bold' }}>Player 1</div>
                                                    <div style={relatedPlayerStyle(
                                                        nextMatch,
                                                        nextMatch.participants[0],
                                                        Boolean(currentMatch && isMatchWinner(currentMatch, nextMatch.participants[0]?.id)),
                                                    )}>
                                                        {matchParticipantDisplayName(nextMatch.participants[0], nextMatch.state)}
                                                    </div>
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 'bold' }}>Player 2</div>
                                                    <div style={relatedPlayerStyle(
                                                        nextMatch,
                                                        nextMatch.participants[1],
                                                        Boolean(currentMatch && isMatchWinner(currentMatch, nextMatch.participants[1]?.id)),
                                                    )}>
                                                        {matchParticipantDisplayName(nextMatch.participants[1], nextMatch.state)}
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    )}

                                    {nextLoserMatch && (
                                        <Card title="Next Match (Loser)" size="small">
                                            <div style={{ marginBottom: 16 }}>
                                                <Space>
                                                    {nextLoserMatch.bout_number ? <Tag>BOUT {nextLoserMatch.bout_number}</Tag> : null}
                                                    {nextLoserMatch.court ? <Tag>Court {nextLoserMatch.court}</Tag> : null}
                                                    <Tag color="blue">Date: {nextLoserMatch.plan_date || '-'}</Tag>
                                                    <Tag color="blue">Time: {formatTime(nextLoserMatch.plan_start_at)}</Tag>
                                                </Space>
                                                <div style={{ marginTop: 8 }}>
                                                    <Space>
                                                        {nextLoserMatch.round !== undefined && (
                                                            <Tag color="purple">{getRoundTitle(nextLoserMatch)}</Tag>
                                                        )}
                                                    </Space>
                                                </div>
                                                <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                                                    ID: {nextLoserMatch.id}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 'bold' }}>Player 1</div>
                                                    <div style={relatedPlayerStyle(
                                                        nextLoserMatch,
                                                        nextLoserMatch.participants[0],
                                                        Boolean(currentMatch && isMatchWinner(currentMatch, nextLoserMatch.participants[0]?.id)),
                                                    )}>
                                                        {matchParticipantDisplayName(nextLoserMatch.participants[0], nextLoserMatch.state)}
                                                    </div>
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 'bold' }}>Player 2</div>
                                                    <div style={relatedPlayerStyle(
                                                        nextLoserMatch,
                                                        nextLoserMatch.participants[1],
                                                        Boolean(currentMatch && isMatchWinner(currentMatch, nextLoserMatch.participants[1]?.id)),
                                                    )}>
                                                        {matchParticipantDisplayName(nextLoserMatch.participants[1], nextLoserMatch.state)}
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    )}
                                </div>
                            ),
                        }] : [])
                    ]}
                />
                {activeTab === '1' && (
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        marginTop: 24,
                        paddingTop: 16,
                        borderTop: '1px solid #f0f0f0'
                    }}>
                        <Space>
                            <Button type="primary" loading={savingState} onClick={handleEditSubmit}>
                                Save
                            </Button>
                            <Button onClick={onCancel}>
                                Cancel
                            </Button>
                        </Space>
                        <Button
                            danger
                            loading={savingState}
                            disabled={currentMatch?.state === 'bye'}
                            onClick={handleResetMatch}
                        >
                            Reset Match
                        </Button>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default EditMatchModal;
